import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, Stack, TextField, MenuItem, CircularProgress } from '@mui/material';
import React, { useState, useEffect } from 'react';
import ConfirmDialog from 'src/components/confirm-dialog';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { useNavigate } from 'react-router-dom';
import { getTankResources, deleteTankResource, updateTankResource, getTankCategories } from 'src/api';

interface TankSecondType {
  id: string;
  key: string;
  category: any;
  current_version: string;
  latest_version: string;
}

const DEFAULT_ROWS_PER_PAGE = 5;

export default function TankTypeSecondList() {
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = useState(0);
  const [delItem, setDelItem] = useState<TankSecondType | null>(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [selectedParentCategory, setSelectedParentCategory] = useState(''); // 选中的一级分类筛选
  const [openPublish, setOpenPublish] = useState(false);
  const [publishItem, setPublishItem] = useState<TankSecondType | null>(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [editItem, setEditItem] = useState<TankSecondType | null>(null);
  const [loading, setLoading] = useState(false);
  const [parentTypeOptions, setParentTypeOptions] = useState<{label: string, value: string}[]>([]);
  const navigate = useNavigate();

  // 添加表单
  // const addDefaultValues = { name: '', parentName: '', desc: '' };
  // const addMethods = useForm({ defaultValues: addDefaultValues });
  // const { reset: resetAdd, handleSubmit: handleAddSubmit } = addMethods;

  // 发布新版本表单
  const publishDefaultValues = { version: '' };
  const publishMethods = useForm({ defaultValues: publishDefaultValues });
  const { reset: resetPublish, handleSubmit: handlePublishSubmit } = publishMethods;

  // 编辑表单
  const editDefaultValues = { category_id: '' };
  const editMethods = useForm({ defaultValues: editDefaultValues });
  const { reset: resetEdit, handleSubmit: handleEditSubmit } = editMethods;

  // 获取二级分类列表 - 支持分页和搜索
  const fetchResources = async (currentPage: number, pageSize: number, searchKeyword: string = '', parentCategoryId: string = '') => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage + 1,
        pageSize: pageSize
      };
      
      // 如果有搜索关键词，添加到参数中
      if (searchKeyword.trim()) {
        params.name = searchKeyword.trim();
      }
      
      if (parentCategoryId) {
        params.category_id = parentCategoryId;
      }
      
      const response: any = await getTankResources(params);
      
      // 根据实际API响应格式调整
      if (response.code === 200 && response.data && response.data.data) {
        const resources = response.data.data;
        setRows(Array.isArray(resources) ? resources : []);
        setTotal(resources.length); // 如果后端返回总数，这里需要调整
      } else {
        setRows([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('获取二级分类列表失败:', error);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // 获取一级分类选项
  const fetchParentCategories = async () => {
    try {
      const response: any = await getTankCategories();
      if (response.code === 0 && response.data && response.data.data) {
        const categories = response.data.data;
        const options = categories.map((cat: any) => ({
          label: cat.name,
          value: cat.id  // 使用ID作为value
        }));
        setParentTypeOptions(options);
      }
    } catch (error) {
      console.error('获取一级分类失败:', error);
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    fetchParentCategories();
  }, []);

  // 分页变化时重新获取数据
  useEffect(() => {
    fetchResources(page, rowsPerPage, searchName, selectedParentCategory);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };
  const handleDelete = (item: TankSecondType) => {
    setDelItem(item);
    setOpenConfirm(true);
  };
  
  const handleConfirmDelete = async () => {
    if (delItem) {
      try {
        await deleteTankResource(delItem.key);
        
        // 删除后检查当前页是否还有数据
        const newTotal = total - 1;
        const maxPage = Math.max(0, Math.ceil(newTotal / rowsPerPage) - 1);
        const targetPage = page > maxPage ? maxPage : page;
        
        if (targetPage !== page) {
          setPage(targetPage);
        }
        
        await fetchResources(targetPage, rowsPerPage, searchName, selectedParentCategory);
        setOpenConfirm(false);
        setDelItem(null);
      } catch (error) {
        console.error('删除二级分类失败:', error);
      }
    }
  };
  
  // 处理一级分类筛选变化
  const handleParentCategoryChange = (value: string) => {
    setSelectedParentCategory(value);
  };

  // 搜索
  const handleSearch = () => {
    setPage(0); // 重置到第一页
    fetchResources(0, rowsPerPage, searchName, selectedParentCategory);
  };
  
  const handleReset = () => {
    setSearchName('');
    setSelectedParentCategory('');
    setPage(0);
    fetchResources(0, rowsPerPage, '', '');
  };
  // 添加
  // const handleAdd = (data: { name: string; parentName: string; desc: string }) => { ... };
  // 发布新版本
  const handlePublish = async (data: { version: string }) => {
    if (publishItem) {
      try {
        // 这里调用发布API，具体根据后端实现调整
        // await publishTankResource(publishItem.id, data);
        
        // 临时处理：直接更新本地数据
        const newRows = rows.map(row =>
          row.id === publishItem.id ? { ...row, latestVersion: data.version } : row
        );
        setRows(newRows);
        setOpenPublish(false);
        setPublishItem(null);
        resetPublish();
        
        // 如果后端支持发布接口，取消注释下面这行，删除上面的临时处理
        // await fetchResources(page, rowsPerPage, searchName, selectedParentCategory);
      } catch (error) {
        console.error('发布新版本失败:', error);
      }
    }
  };
  
  const handleEditSave = async (data: { category_id: string }) => {
    if (editItem) {
      try {
        await updateTankResource(editItem.key, { category_id: data.category_id });
        await fetchResources(page, rowsPerPage, searchName, selectedParentCategory);
        setOpenEdit(false);
        setEditItem(null);
        resetEdit();
      } catch (error) {
        console.error('编辑二级分类失败:', error);
      }
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" component="h2" sx={{ mb: 3 }}>
        鱼缸二级分类
      </Typography>
      
      {/* 筛选和搜索区域 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          {/* <TextField
            label="名称搜索"
            size="small"
            value={searchName}
            onChange={e => setSearchName(e.target.value)}
            sx={{ width: 200 }}
            placeholder="请输入分类名称"
          /> */}
          <TextField
            label="所属一级分类"
            size="small"
            value={selectedParentCategory}
            onChange={e => handleParentCategoryChange(e.target.value)}
            select
            sx={{ width: 200 }}
          >
            <MenuItem value="">全部分类</MenuItem>
            {parentTypeOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={handleSearch}>搜索</Button>
          <Button variant="outlined" onClick={handleReset}>重置</Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" onClick={() => navigate('/dashboard/tankType/addSecond')}>
            添加
          </Button>
        </Stack>
      </Paper>
      
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 900 }} aria-label="tank second type table">
            <TableHead>
              <TableRow>
                <TableCell align="center">分类名称</TableCell>
                <TableCell align="center">所属一级分类</TableCell>
                {/* <TableCell align="center">描述</TableCell> */}
                <TableCell align="center">当前发布版本</TableCell>
                <TableCell align="center">最新版本</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rows.length > 0 ? (
                rows.map((row, index) => (
                  <TableRow key={row.id || `row-${index}`}>
                    <TableCell align="center">{row.type_mappings[0].display_name}</TableCell>
                    <TableCell align="center">{row.category.name}</TableCell>
                    {/* <TableCell align="center">{row.desc}</TableCell> */}
                    <TableCell align="center">{row.current_version}</TableCell>
                    <TableCell align="center">{row.latest_version}</TableCell>
                    <TableCell align="center">
                      <Button variant="text" onClick={() => navigate(`/dashboard/tankType/secondDetail?key=${row.key}`)}>查看</Button>
                      {/* <Button variant="text" onClick={() => handleEdit(row)}>编辑</Button> */}
                      <Button variant="text" color="primary" onClick={() => navigate(`/dashboard/tankType/addSecond?key=${row.key}`)}>发布新版</Button>
                      <Button variant="text" color="error" onClick={() => handleDelete(row)}>删除</Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">暂无数据</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={'每页条数'}
        />
      </Paper>
      {/* 删除确认 */}
      <ConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title="删除"
        content={`确定要删除分类"${delItem?.key}"吗？`}
        action={
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            删除
          </Button>
        }
      />
      {/* 发布新版本弹窗 */}
      <ConfirmDialog
        open={openPublish}
        onClose={() => { setOpenPublish(false); setPublishItem(null); resetPublish(); }}
        title="发布新版本"
        content={
          <FormProvider methods={publishMethods} onSubmit={handlePublishSubmit(handlePublish)}>
            <RHFTextField name="version" label="新版本号" sx={{ mt: 2, mb: 1 }} />
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>
              确认发布
            </Button>
          </FormProvider>
        }
        action={null}
      />
      {/* 编辑弹窗 */}
      <ConfirmDialog
        open={openEdit}
        onClose={() => { setOpenEdit(false); setEditItem(null); resetEdit(); }}
        title="编辑所属一级分类"
        content={
          <FormProvider methods={editMethods} onSubmit={handleEditSubmit(handleEditSave)}>
            <RHFTextField name="category_id" label="所属一级分类" select sx={{ mt: 2, mb: 1 }}>
              <MenuItem value="">请选择</MenuItem>
              {parentTypeOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </RHFTextField>
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>
              确认修改
            </Button>
          </FormProvider>
        }
        action={null}
      />
    </Box>
  );
}